import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { itemImageUrl } from '../../api/client';
import type { LibraryItem, PlaybackProgress } from '../../api/types';
import { TileProgress } from './WatchProgress';

interface EpisodeListProps {
  episodes: LibraryItem[];
  progress: Record<string, PlaybackProgress> | undefined;
  /// Starts an episode in the player without leaving the page it was started from.
  onPlay: (episode: LibraryItem) => void;
}

/// The episodes of a season, as rows you can actually do something with: open one to read
/// about it, or press play and stay where you are. The still, what it is about, how far in you
/// got, and whether you have seen it.
export default function EpisodeList({ episodes, progress, onPlay }: EpisodeListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    // A measure, so a description does not run the width of a television.
    <Stack spacing={1} sx={{ maxWidth: 1100 }}>
      {episodes.map((episode) => {
        const still = itemImageUrl(episode);
        const seen = progress?.[episode.id];

        return (
          <Stack
            key={episode.id}
            direction="row"
            spacing={2}
            onClick={() => navigate(`/items/${episode.id}`)}
            sx={{
              p: 1.5,
              borderRadius: 2,
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'background-color 160ms ease',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: 120, sm: 160 },
                aspectRatio: '16 / 9',
                borderRadius: 1.5,
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: 'background.paper',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {still ? (
                <Box
                  component="img"
                  src={still}
                  alt=""
                  loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography sx={{ fontSize: 28, fontWeight: 700, opacity: 0.4 }}>
                  {episode.episodeNumber ?? ''}
                </Typography>
              )}

              {seen && seen.fraction > 0 && <TileProgress fraction={seen.fraction} />}
            </Box>

            <Stack spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="subtitle2" noWrap>
                  {episode.episodeNumber !== null ? `${episode.episodeNumber}. ` : ''}
                  {episode.title}
                </Typography>

                {seen?.isWatched && (
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary' }}
                    aria-label={t('item.watched')}
                  />
                )}
              </Stack>

              {episode.overview && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {episode.overview}
                </Typography>
              )}
            </Stack>

            {episode.isPlayable && (
              <IconButton
                aria-label={t('item.play')}
                onClick={(event) => {
                  // The row navigates; the button plays. Without this the click does both.
                  event.stopPropagation();
                  onPlay(episode);
                }}
                sx={{ flexShrink: 0 }}
              >
                <PlayArrowIcon />
              </IconButton>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
